import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { UserPlus, X, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';

const DISMISS_KEY = 'enable_guests_banner_dismissed_until';

/**
 * Host-only prompt: offers a one-click way to enable +1 guest RSVPs on all
 * upcoming events that don't have it turned on yet. Dismissible for 7 days.
 */
const EnableGuestsBanner = () => {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [enablingId, setEnablingId] = useState(null);

  const isDismissed = () => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (!raw) return false;
      return Date.now() < parseInt(raw, 10);
    } catch {
      return false;
    }
  };

  const fetchPending = async () => {
    try {
      const res = await axios.get(`${API}/events/my-events/needs-guest-config`);
      setPending(res.data?.events || []);
    } catch (err) {
      // Silent — banner just won't render
      setPending([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isDismissed()) {
      setLoading(false);
      return;
    }
    fetchPending();
  }, []);

  const handleEnableAll = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/events/my-events/enable-guests-bulk`, {});
      toast.success(`+1 Guests enabled on ${res.data?.updated_count ?? 0} events`);
      setPending([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to enable guests');
    } finally {
      setBusy(false);
    }
  };

  const handleEnableOne = async (eventId, title) => {
    setEnablingId(eventId);
    try {
      await axios.patch(`${API}/events/${eventId}/enable-guests`, {});
      toast.success(`+1 Guests enabled on "${title}"`);
      setPending((prev) => prev.filter((e) => e.event_id !== eventId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setEnablingId(null);
    }
  };

  const handleDismiss = () => {
    try {
      // Dismiss for 7 days
      const until = Date.now() + 7 * 24 * 60 * 60 * 1000;
      localStorage.setItem(DISMISS_KEY, String(until));
    } catch {}
    setPending([]);
  };

  if (loading || pending.length === 0) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.25 }}
        className="mb-6 rounded-2xl border-2 border-primary/20 bg-gradient-to-r from-primary/5 via-accent/10 to-primary/5 p-5 relative overflow-hidden"
        data-testid="enable-guests-banner"
      >
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-black/5 transition-colors"
          aria-label="Dismiss for 7 days"
          data-testid="enable-guests-dismiss-btn"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>

        <div className="flex items-start gap-4">
          <div className="hidden sm:flex w-11 h-11 rounded-full bg-primary/10 items-center justify-center shrink-0">
            <UserPlus className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-primary" />
              <h3 className="font-heading font-bold text-base">
                Let attendees bring a +1 to your events
              </h3>
            </div>
            <p className="text-sm text-muted-foreground mb-3 pr-8">
              You have <strong className="text-foreground">{pending.length}</strong>{' '}
              upcoming {pending.length === 1 ? 'event' : 'events'} without +1 guest RSVPs enabled.
              Turn it on so members can bring a partner or friend.
            </p>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                onClick={handleEnableAll}
                disabled={busy}
                className="rounded-full font-semibold"
                data-testid="enable-guests-all-btn"
              >
                {busy ? 'Enabling…' : `Enable on all ${pending.length}`}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setExpanded((v) => !v)}
                className="rounded-full font-semibold"
                data-testid="enable-guests-expand-btn"
              >
                Choose per event
                {expanded ? (
                  <ChevronUp className="w-4 h-4 ml-1" />
                ) : (
                  <ChevronDown className="w-4 h-4 ml-1" />
                )}
              </Button>
            </div>

            <AnimatePresence>
              {expanded && (
                <motion.ul
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="mt-4 space-y-2 overflow-hidden"
                  data-testid="enable-guests-per-event-list"
                >
                  {pending.map((ev) => (
                    <li
                      key={ev.event_id}
                      className="flex items-center justify-between gap-3 py-2 px-3 rounded-lg bg-white/60 border border-border/60"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-sm truncate">{ev.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {ev.event_date} · {ev.city}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEnableOne(ev.event_id, ev.title)}
                        disabled={enablingId === ev.event_id}
                        className="rounded-full text-xs"
                        data-testid={`enable-guests-one-${ev.event_id}`}
                      >
                        {enablingId === ev.event_id ? 'Enabling…' : 'Enable'}
                      </Button>
                    </li>
                  ))}
                </motion.ul>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default EnableGuestsBanner;
